// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@pace/App/Store/Reducers";
import * as cssLinter from "css";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    linksSectionTitle = "linksSectionTitle",
    links = "links",
    customCSS = "customCSS",
}

interface CustomLink {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        borderBottom?: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.linksSectionTitle]: string;
        [fields.links]: CustomLink[];
        [fields.customCSS]: string;
    };
}

export const dashboardMapLinks = (state: ApplicationState, widgetLinks: CustomLink[]) => {
    const myAccountPage = getPageLinkByPageType(state, "MyAccountPage");
    const isIncluded = (pageLink: any): boolean =>
        pageLink?.parentId === myAccountPage?.id ? !pageLink?.excludeFromNavigation : true;
    const links: any[] = [];
    widgetLinks.forEach(widgetLink => {
        if (widgetLink.fields.destination.type === "Page") {
            const pageLink = getPageLinkByNodeId(state, widgetLink.fields.destination.value);
            if (pageLink && isIncluded(pageLink)) {
                links.push({
                    ...pageLink,
                    title: widgetLink.fields.overriddenTitle || pageLink?.title,
                    openInNewWindow: widgetLink.fields.openInNewWindow,
                    borderBottom: widgetLink.fields?.borderBottom,
                });
            }
        } else if (widgetLink.fields.destination.type === "Url") {
            const url = widgetLink.fields.destination.value;
            const title = widgetLink.fields.overriddenTitle;

            links.push({ id: "", url, title, borderBottom: widgetLink.fields?.borderBottom });
        }
    });

    return links;
};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const quickLinks = dashboardMapLinks(state, ownProps.fields.links);

    return {
        quickLinks: quickLinks || {},
        currentPageLink: getPageLinkByNodeId(state, getCurrentPage(state).nodeId)?.url || "",
    };
};

export interface MyAccountDashboardLinksStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const myAccountDashboardLinksStyles: MyAccountDashboardLinksStyles = {};
const styles = myAccountDashboardLinksStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const MyAccountDashboardLinks = (props: Props) => {
    if (!props.quickLinks) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${props.fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="navigation-list" data-test-selector="MyAccountDashboardLinks">
                {props.fields.linksSectionTitle && props.quickLinks.length > 0 && (
                    <Typography {...styles.title} className="navigation-list-title">
                        {props.fields.linksSectionTitle}
                    </Typography>
                )}

                <StyledWrapper {...styles.container}>
                    {props.quickLinks.map(
                        (link: any, index) =>
                            link?.url && (
                                <StyledWrapper className="navigation-list-item" key={`${link?.url}${index}`}>
                                    <Link
                                        className={`navigation-list-link ${
                                            props.currentPageLink === link.url && "active"
                                        }`}
                                        key={`${link?.title}_${link.url}`}
                                        href={link?.url}
                                        {...styles.link}
                                        target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                        aria-label={link?.title}
                                    >
                                        {link?.title}
                                    </Link>
                                    {link?.borderBottom && <StyledWrapper {...styles.linkWrapper}></StyledWrapper>}
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-title {
}

.navigation-list-item {
}

.navigation-list-link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(MyAccountDashboardLinks),
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.linksSectionTitle,
                fieldType: "Contextual",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Quick Links",
                tab: basicTab,
            },
            {
                displayName: "Links",
                name: fields.links,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                tab: basicTab,
                getDisplay: (item: HasFields, state: any) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    return overriddenTitle;
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: (item: HasFields) => false,
                        allowUrls: (item: HasFields) => true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        displayName: "Open in New Window",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "borderBottom",
                        displayName: "Add a Seperator",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
